import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Button,
  Card,
  Checkbox,
  Descriptions,
  Form,
  Input,
  Modal,
  Popconfirm,
  Select,
  Space,
  Table,
  Tag,
  Typography,
  message,
} from 'antd';
import { ArrowLeftOutlined, PlusOutlined, UserAddOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { schemaProfileService, tenantService } from '../../api/services';
import { loadSchemaGitSettings } from '../../storage/schemaGitSettings';
import type { TenantRosterMember } from '../../api/services/memberApi';
import type { TenantServiceAccount } from '../../api/services/tenantApi';
import { useAdminScope } from '../../context/AdminScopeContext';
import type { Tenant } from '../../types/tenant';
import type { SchemaProfile } from '../../types/schemaProfile';

export function TenantDetailPage() {
  const { tenantId } = useParams<{ tenantId: string }>();
  const navigate = useNavigate();
  const { scope } = useAdminScope();
  const [tenant, setTenant] = useState<Tenant | null>(null);
  const [users, setUsers] = useState<TenantRosterMember[]>([]);
  const [serviceAccounts, setServiceAccounts] = useState<TenantServiceAccount[]>([]);
  const [schemaProfiles, setSchemaProfiles] = useState<SchemaProfile[]>([]);
  const [boundProfileId, setBoundProfileId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [userModalOpen, setUserModalOpen] = useState(false);
  const [saModalOpen, setSaModalOpen] = useState(false);
  const [createdSecret, setCreatedSecret] = useState<TenantServiceAccount | null>(null);
  const [userForm] = Form.useForm();
  const [saForm] = Form.useForm();
  const createInKeycloak = Form.useWatch('createInKeycloak', userForm);

  const loadData = useCallback(async () => {
    if (!tenantId) return;
    setLoading(true);
    try {
      const [t, roster, profileResult, sas] = await Promise.all([
        tenantService.get(tenantId),
        tenantService.getRoster(tenantId),
        schemaProfileService.list(loadSchemaGitSettings()),
        tenantService.listServiceAccounts(tenantId),
      ]);

      if (!t) {
        message.error('租户不存在');
        navigate('/tenants');
        return;
      }

      setTenant(t);
      setUsers(roster);
      setServiceAccounts(sas);
      setSchemaProfiles(profileResult.ok ? profileResult.profiles : []);
      setBoundProfileId(t.schemaProfile ?? null);
    } catch (err) {
      message.error(err instanceof Error ? err.message : '加载失败');
    } finally {
      setLoading(false);
    }
  }, [tenantId, navigate]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleAddUser = async () => {
    if (!tenantId) return;
    const values = await userForm.validateFields();
    try {
      const result = await tenantService.addUser({
        tenantId,
        email: values.email,
        username: values.username,
        name: values.name,
        role: values.role,
        createInKeycloak: values.createInKeycloak,
        password: values.password,
      });

      const hints: string[] = [];
      if (result.created) hints.push('已创建系统用户');
      if (result.keycloakCreated) hints.push('已在 Keycloak 创建用户');
      message.success(hints.length ? `添加成功：${hints.join('、')}` : '添加成功');
      setUserModalOpen(false);
      userForm.resetFields();
      loadData();
    } catch (err) {
      message.error(err instanceof Error ? err.message : '添加失败');
    }
  };

  const handleCreateServiceAccount = async () => {
    if (!tenantId) return;
    const values = await saForm.validateFields();
    try {
      const created = await tenantService.createServiceAccount(tenantId, {
        clientId: values.clientId.trim(),
        name: values.name.trim(),
        email: values.email?.trim() || undefined,
        username: values.username?.trim() || undefined,
      });
      setSaModalOpen(false);
      saForm.resetFields();
      setCreatedSecret(created);
      message.success('服务账号已创建');
      loadData();
    } catch (err) {
      message.error(err instanceof Error ? err.message : '创建失败');
    }
  };

  const handleRemoveUser = async (userId: string, role: 'admin' | 'member') => {
    if (!tenantId) return;
    try {
      if (role === 'admin') {
        await tenantService.removeAdmin(tenantId, userId);
      } else {
        await tenantService.removeMember(tenantId, userId);
      }
      message.success('已移除');
      loadData();
    } catch (err) {
      message.error(err instanceof Error ? err.message : '移除失败');
    }
  };

  const handleBindProfile = async (profileId: string | null) => {
    if (!tenantId) return;
    try {
      if (profileId) {
        const updated = await tenantService.setSchemaProfile(tenantId, profileId);
        message.success('Schema Profile 已绑定');
        setBoundProfileId(updated.schemaProfile);
      } else {
        await tenantService.clearSchemaProfile(tenantId);
        message.success('已解除绑定');
        setBoundProfileId(profileId);
      }
    } catch (err) {
      message.error(err instanceof Error ? err.message : '操作失败');
    }
  };

  const userColumns: ColumnsType<TenantRosterMember> = [
    { title: '姓名', dataIndex: 'name', key: 'name' },
    { title: '用户名', dataIndex: 'username', key: 'username' },
    { title: '邮箱', dataIndex: 'email', key: 'email' },
    {
      title: '角色',
      dataIndex: 'role',
      key: 'role',
      render: (role: 'admin' | 'member') => (
        <Tag color={role === 'admin' ? 'gold' : 'default'}>
          {role === 'admin' ? '管理员' : '成员'}
        </Tag>
      ),
    },
    {
      title: '操作',
      key: 'action',
      render: (_, record) => (
        <Popconfirm title="确认移除该用户？" onConfirm={() => handleRemoveUser(record.id, record.role)}>
          <Button type="link" danger>
            {record.role === 'admin' ? '移除管理员' : '移除成员'}
          </Button>
        </Popconfirm>
      ),
    },
  ];

  const saColumns: ColumnsType<TenantServiceAccount> = [
    { title: '名称', dataIndex: 'name', key: 'name' },
    { title: 'Client ID', dataIndex: 'clientId', key: 'clientId' },
    { title: '用户名', dataIndex: 'username', key: 'username' },
    { title: '邮箱', dataIndex: 'email', key: 'email' },
    {
      title: 'User ID',
      dataIndex: 'userId',
      key: 'userId',
      render: (id: string) => (
        <Typography.Text copyable style={{ fontSize: 12 }}>
          {id}
        </Typography.Text>
      ),
    },
  ];

  if (!tenant) {
    return null;
  }

  return (
    <div>
      <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/tenants')} style={{ marginBottom: 16 }}>
        返回列表
      </Button>

      <Card
        title="租户信息"
        style={{ marginBottom: 16 }}
        extra={
          scope?.isPlatformAdmin ? (
            <Button onClick={() => navigate(`/tenants/${tenantId}/spaces`)}>
              空间管理
            </Button>
          ) : null
        }
      >
        <Descriptions column={{ xs: 1, sm: 2 }}>
          <Descriptions.Item label="名称">{tenant.name}</Descriptions.Item>
          <Descriptions.Item label="编码">{tenant.code}</Descriptions.Item>
          <Descriptions.Item label="邮箱">{tenant.email}</Descriptions.Item>
          <Descriptions.Item label="类型">
            {tenant.type === 'ORGANIZATION' ? '组织' : '个人'}
          </Descriptions.Item>
        </Descriptions>
      </Card>

      <Card
        title="Schema Profile 绑定"
        style={{ marginBottom: 16 }}
        extra={
          boundProfileId ? (
            <Button type="link" onClick={() => navigate(`/schema-profiles/${boundProfileId}`)}>
              编辑 Schema
            </Button>
          ) : null
        }
      >
        <Space>
          <Select
            style={{ width: 280 }}
            placeholder="选择 Schema Profile"
            allowClear
            value={boundProfileId}
            onChange={handleBindProfile}
            options={schemaProfiles.map((p) => ({ value: p.id, label: `${p.name} (${p.id})` }))}
          />
          {boundProfileId && (
            <Tag color="blue">已绑定: {boundProfileId}</Tag>
          )}
        </Space>
      </Card>

      <Card
        title="成员与管理员"
        style={{ marginBottom: 16 }}
        extra={
          <Button type="primary" icon={<UserAddOutlined />} onClick={() => setUserModalOpen(true)}>
            添加用户
          </Button>
        }
      >
        <Table rowKey="id" loading={loading} columns={userColumns} dataSource={users} pagination={false} />
      </Card>

      <Card
        title="服务账号"
        extra={
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setSaModalOpen(true)}>
            创建服务账号
          </Button>
        }
      >
        <Typography.Paragraph type="secondary" style={{ marginBottom: 12 }}>
          供产品后端（如层流 service）使用的 Keycloak 机密 Client。创建后 JWT 自动带该租户的
          tenant-context。跨空间 Trusted 调用仍需把 Client ID 加入 pydc / dc-auth 的
          TRUSTED_SERVICE_CLIENT_IDS。
        </Typography.Paragraph>
        <Table
          rowKey="userId"
          loading={loading}
          columns={saColumns}
          dataSource={serviceAccounts}
          pagination={false}
          locale={{ emptyText: '暂无服务账号' }}
        />
      </Card>

      <Modal
        title="添加租户用户"
        open={userModalOpen}
        onCancel={() => setUserModalOpen(false)}
        onOk={handleAddUser}
        okText="添加"
        destroyOnHidden
      >
        <Form
          form={userForm}
          layout="vertical"
          initialValues={{ role: 'member', createInKeycloak: true }}
        >
          <Form.Item name="name" label="姓名" rules={[{ required: true, message: '请输入姓名' }]}>
            <Input />
          </Form.Item>
          <Form.Item name="username" label="用户名">
            <Input placeholder="留空则使用邮箱前缀" />
          </Form.Item>
          <Form.Item
            name="email"
            label="邮箱"
            rules={[{ required: true, type: 'email', message: '请输入有效邮箱' }]}
          >
            <Input />
          </Form.Item>
          <Form.Item name="role" label="角色" rules={[{ required: true }]}>
            <Select
              options={[
                { value: 'admin', label: '管理员' },
                { value: 'member', label: '成员' },
              ]}
            />
          </Form.Item>
          <Form.Item name="createInKeycloak" valuePropName="checked">
            <Checkbox>用户不存在时在 Keycloak 创建</Checkbox>
          </Form.Item>
          {createInKeycloak && (
            <Form.Item
              name="password"
              label="初始密码"
              rules={[{ required: true, message: '创建 Keycloak 用户时需填写初始密码' }]}
            >
              <Input.Password placeholder="新用户首次登录需修改" />
            </Form.Item>
          )}
        </Form>
      </Modal>

      <Modal
        title="创建租户服务账号"
        open={saModalOpen}
        onCancel={() => setSaModalOpen(false)}
        onOk={handleCreateServiceAccount}
        okText="创建"
        destroyOnHidden
      >
        <Form form={saForm} layout="vertical">
          <Form.Item
            name="clientId"
            label="Client ID"
            rules={[
              { required: true, message: '请输入 Client ID' },
              {
                pattern: /^[a-zA-Z0-9._-]+$/,
                message: '仅允许字母、数字、. _ -',
              },
            ]}
          >
            <Input placeholder="例如 cengliu-service" />
          </Form.Item>
          <Form.Item name="name" label="显示名称" rules={[{ required: true, message: '请输入名称' }]}>
            <Input placeholder="例如 层流服务" />
          </Form.Item>
          <Form.Item name="username" label="用户名">
            <Input placeholder="默认与 Client ID 相同" />
          </Form.Item>
          <Form.Item name="email" label="邮箱" rules={[{ type: 'email', message: '请输入有效邮箱' }]}>
            <Input placeholder="省略则自动生成" />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="请保存 Client Secret"
        open={Boolean(createdSecret)}
        onCancel={() => setCreatedSecret(null)}
        onOk={() => setCreatedSecret(null)}
        okText="已保存"
        cancelButtonProps={{ style: { display: 'none' } }}
      >
        {createdSecret && (
          <>
            <Alert
              type="warning"
              showIcon
              style={{ marginBottom: 16 }}
              message="Secret 仅此一次展示，关闭后无法再次查看。"
            />
            <Descriptions column={1} size="small" bordered>
              <Descriptions.Item label="Client ID">
                <Typography.Text copyable>{createdSecret.clientId}</Typography.Text>
              </Descriptions.Item>
              <Descriptions.Item label="Client Secret">
                <Typography.Text copyable>{createdSecret.clientSecret}</Typography.Text>
              </Descriptions.Item>
              <Descriptions.Item label="User ID">
                <Typography.Text copyable>{createdSecret.userId}</Typography.Text>
              </Descriptions.Item>
            </Descriptions>
          </>
        )}
      </Modal>
    </div>
  );
}
